/**
 * Socket Service - WebSocket Connection Manager (Singleton)
 * 
 * Gestiona la conexión única con Games API (puerto 5001) vía Socket.IO.
 * 
 * @see src/hooks/useSocket.js - React hook que consume este servicio
 * @see games_api/core/factory.py - Backend WebSocket server
 * 
 * Responsabilidad: Mantener conexión WebSocket, emit/on/off methods.
 * NO contiene lógica de juego ni estado de UI.
 * 
 * @example
 * import SocketService from './services/socketService';
 * 
 * const socket = SocketService.getInstance();
 * socket.connect(token);
 * socket.on('room_created', (data) => console.log(data));
 * socket.emit('create_room', { room_id: 'abc123' });
 */

import { io } from 'socket.io-client';
import { GAMES_API_URL } from '../constants';

class SocketService {
    constructor() {
        if (SocketService.instance) {
            return SocketService.instance;
        }

        this.socket = null;
        this.isConnecting = false;
        SocketService.instance = this;
    }

    /**
     * Get singleton instance
     * @returns {SocketService}
     */
    static getInstance() {
        if (!SocketService.instance) {
            SocketService.instance = new SocketService();
        }
        return SocketService.instance;
    }

    /**
     * Connect to Games API WebSocket server
     * 
     * @param {string} token - JWT token for authentication
     * @returns {object} Socket instance
     */
    connect(token) {
        if (this.socket && this.socket.connected) {
            console.log('[SocketService] Already connected');
            return this.socket;
        }

        if (this.isConnecting) {
            console.log('[SocketService] Connection in progress...');
            return this.socket;
        }

        this.isConnecting = true;

        console.log(`[SocketService] Connecting to ${GAMES_API_URL}...`);

        this.socket = io(GAMES_API_URL, {
            auth: {
                token: token
            },
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5,
            transports: ['websocket', 'polling']
        });

        // Internal event listeners
        this.socket.on('connect', () => {
            console.log('[SocketService] ✅ Connected successfully. SID:', this.socket.id);
            this.isConnecting = false;
        });

        this.socket.on('disconnect', (reason) => {
            console.warn('[SocketService] ⚠️ Disconnected. Reason:', reason);
            this.isConnecting = false;
        });

        this.socket.on('connect_error', (error) => {
            console.error('[SocketService] ❌ Connection error:', error.message);
            this.isConnecting = false;
        });

        this.socket.on('error', (error) => {
            console.error('[SocketService] ❌ Socket error:', error);
        });

        return this.socket;
    }

    /**
     * Disconnect from server
     */
    disconnect() {
        if (this.socket) {
            console.log('[SocketService] Disconnecting...');
            this.socket.disconnect();
            this.socket = null;
            this.isConnecting = false;
        }
    }

    /**
     * Emit event to server
     * 
     * @param {string} event - Event name
     * @param {object} data - Event data
     * @param {function} callback - Optional acknowledgement callback
     */
    emit(event, data, callback) {
        if (!this.socket) {
            console.error('[SocketService] Cannot emit: not connected');
            return;
        }

        if (callback) {
            this.socket.emit(event, data, callback);
        } else {
            this.socket.emit(event, data);
        }
    }

    /**
     * Subscribe to server event
     * 
     * @param {string} event - Event name
     * @param {function} callback - Event handler
     */
    on(event, callback) {
        if (!this.socket) {
            console.error('[SocketService] Cannot subscribe: not connected');
            return;
        }
        this.socket.on(event, callback);
    }

    /**
     * Unsubscribe from server event
     * 
     * @param {string} event - Event name
     * @param {function} callback - Event handler to remove
     */
    off(event, callback) {
        if (!this.socket) {
            return;
        }

        if (callback) {
            this.socket.off(event, callback);
        } else {
            this.socket.off(event);
        }
    }

    /**
     * Check if currently connected
     * @returns {boolean}
     */
    isConnected() {
        return this.socket && this.socket.connected;
    }

    /**
     * Get underlying socket instance
     * @returns {object|null} Socket.IO client instance
     */
    getSocket() {
        return this.socket;
    }
}

export default SocketService;
